import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { userService } from '../services/userService';
import accountService from '../services/accountService';
import api from '../services/api';
import './UserList.css';
import './VaultSourceList.css';

const USER_ROLES = {
  1: 'Site Manager Admin',
  2: 'Site Manager User',
  3: 'Site Manager Config',
  4: 'View Only',
  5: 'User',
  6: 'Admin User',
  7: 'Vault User'
};

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [accountMap, setAccountMap] = useState({});
  const [userChainsMap, setUserChainsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({ activeOnly: false });

  const navigate = useNavigate();

  useEffect(() => {
    loadAccounts();
  }, []);

  useEffect(() => {
    loadUsers();
  }, [filters.activeOnly]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const filterParams = {};
      if (filters.activeOnly) filterParams.activeOnly = true;
      const data = await userService.getAllUsers(filterParams);
      setUsers(data.users || []);
    } catch (err) {
      console.error('Error loading users:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadAccounts = async () => {
    try {
      const [accountsData, mappingsData] = await Promise.all([
        accountService.getAllAccounts(),
        api.get('/permissions?userChains=1').then(r => r.data)
      ]);

      const map = {};
      (accountsData.accounts || []).forEach(a => { map[a.id] = a.name || a.username || String(a.id); });
      setAccountMap(map);

      // Build userId → Set<chainId> from resolved mappings
      const chainsMap = {};
      (mappingsData.mappings || []).forEach(({ userid, chain_id }) => {
        if (!chain_id) return;
        if (!chainsMap[userid]) chainsMap[userid] = new Set();
        chainsMap[userid].add(chain_id);
      });
      setUserChainsMap(chainsMap);
    } catch {
      // non-fatal
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      // Only drop the row when the server says the user is gone — it used to vanish here and return on refresh.
      const res = await userService.deleteUser(userId);
      if (res?.fullyDeleted === false) {
        alert(res.message || 'The user was not deleted');
        return;
      }
      setUsers(prev => prev.filter(user => user.ID !== userId));
    } catch (err) {
      console.error('Error deleting user:', err);
      alert(err.response?.data?.message || 'Failed to delete user');
    }
  };

  const userChains = (userId) => {
    const chainIds = userChainsMap[userId];
    if (!chainIds || chainIds.size === 0) return '—';
    return [...chainIds].map(id => accountMap[id] || String(id)).join(', ');
  };

  const filteredUsers = searchTerm.trim()
    ? users.filter(user => {
        const term = searchTerm.toLowerCase();
        return [
          user.ID,
          user.useruuid,
          user.firstname,
          user.lastname,
          user.loginname,
          userChains(user.ID),
          USER_ROLES[user.USERROLE] || user.USERROLE,
          user.inactivedate ? 'inactive' : 'active'
        ].some(v => String(v ?? '').toLowerCase().includes(term));
      })
    : users;

  if (loading) {
    return <div className="loading">Loading users...</div>;
  }

  return (
    <div className="user-list-container">
      <div className="header">
        <h1>User Management</h1>
        <button className="btn btn-primary" onClick={() => navigate('/users/new')}>
          Create New User
        </button>
      </div>

      <div className="search-bar">
        <input
          type="text"
          placeholder="Search all fields..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select value={filters.activeOnly ? 'active' : 'all'} onChange={(e) => setFilters({ activeOnly: e.target.value === 'active' })}>
          <option value="all">All users</option>
          <option value="active">Active only</option>
        </select>
        {(searchTerm || filters.activeOnly) && (
          <button className="btn btn-secondary" onClick={() => {
            setSearchTerm('');
            setFilters({ activeOnly: false });
            loadUsers();
          }}>Clear</button>
        )}
      </div>

      {error && <div className="error">{error}</div>}

      <div className="user-table">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>First Name</th>
              <th>Last Name</th>
              <th>Login Name</th>
              <th>Chains</th>
              <th>Role</th>
              <th>Status</th>
              <th>Last Login</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan="9" className="no-data">No users found</td>
              </tr>
            ) : (
              filteredUsers.map(user => (
                <tr key={user.ID} onClick={() => navigate(`/users/${user.ID}/edit`)} style={{ cursor: 'pointer' }}>
                  <td>{user.ID}</td>
                  <td>{user.firstname}</td>
                  <td>{user.lastname}</td>
                  <td>{user.loginname}</td>
                  <td>{userChains(user.ID)}</td>
                  <td>{USER_ROLES[user.USERROLE] || user.USERROLE || '—'}</td>
                  <td>
                    <span className={`status ${user.inactivedate ? 'inactive' : 'active'}`}>
                      {user.inactivedate ? 'Inactive' : 'Active'}
                    </span>
                  </td>
                  <td>{user.lastlogin ? new Date(user.lastlogin).toLocaleString() : 'Never'}</td>
                  <td>
                    <button
                      className="btn btn-small btn-secondary"
                      onClick={(e) => { e.stopPropagation(); navigate(`/users/${user.ID}/edit`); }}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-small btn-danger"
                      onClick={(e) => { e.stopPropagation(); handleDelete(user.ID); }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UserList;
